import { createBrowserHistory } from "history";
import { waitForInitialSplash } from "./react/utils/splash-gate";
import { startNavProgress, stopNavProgress } from "./nav-progress";

/**
 * Retornar `true` (ou resolver para `true`) sinaliza a `handle()` que esta
 * passagem foi um redirect DECIDIDO EM RUNTIME (ex.: `guarded()` em main.ts,
 * quando a permissão falha) — não finalizar o ciclo de 1ª renderização aqui;
 * quem finaliza é a chamada reentrante disparada pelo próprio redirect.
 * Retornar `undefined`/void (o que todas as implementações existentes já
 * fazem) continua significando "montagem real". Complementar ao `Route.
 * redirect` estático, que cobre o caso conhecido em tempo de definição.
 */
export type PageRenderer = (pageRoot: HTMLElement) => void | boolean | Promise<void | boolean>;

export interface Route {
  path: string | RegExp;
  key: string;
  breadcrumb: Array<{ label: string; href?: string }>;
  render: PageRenderer;
  /**
   * A própria rota desenha a chrome (sidebar/header) via `DashboardLayout`.
   *
   * **Obrigatório desde o Estágio 4 do Sunset (ADR-009, 2026-08-14).** Era
   * opcional enquanto existia o shell vanilla como fallback; com `shell.ts`
   * removido, uma rota sem `chrome` ficaria sem sidebar/header. Manter o campo
   * obrigatório faz o TypeScript barrar isso na definição da rota, em vez de
   * a tela quebrar em runtime.
   */
  chrome: "react";
  /**
   * Rota de redirect interno da SPA (history.replaceState + popstate
   * sintético dentro do próprio `render()`, ver `defineInternalRedirect` em
   * main.ts). Avisa `handle()` para pular `mountChrome()` nesta passagem e
   * não finalizar o ciclo de 1ª renderização aqui — quem finaliza é a
   * chamada reentrante (síncrona) que o próprio `render()` dispara.
   */
  redirect?: true;
}

/**
 * Spike A (ADR-012) — infraestrutura de navegação, não migração de rota. Troca
 * `window.history`/`window.addEventListener('popstate', ...)` cru por uma
 * única instância de `history` e `history.listen()`. Zero mudança de
 * comportamento observável: as 36 rotas continuam exatamente como estão,
 * nenhum `<Route>`/`<Router>` do react-router é introduzido aqui.
 *
 * **Troca de dependência (2026-07-22, mesma sessão do Spike A) — achado real
 * de bundle, não hipotético:** a 1ª versão usava `UNSAFE_createBrowserHistory`
 * do próprio `react-router` — funcionalmente idêntica, mas `router.ts` é
 * importado estaticamente por `main.ts` (eager, toda navegação), e essa
 * importação não se tree-shakeava do restante do motor de rotas do
 * `react-router` (confirmado: `RouterProvider`/`Outlet`/`useNavigate`/
 * `NavLink`/`DataRouter` apareciam no bundle final). Medido: `main-*.js`
 * cresceu de 135,75 kB para 169,38 kB (+33,6 kB / +12,5 kB gzip) — regressão
 * real contra o baseline do `ADR-011`. Trocado para o pacote `history`
 * (v5.3.0, ~8 kB minificado, dependência própria, sem o motor de rotas
 * junto) — mesma API (`createBrowserHistory().listen()/.push()/.replace()`),
 * notifica listeners sincronamente em `push()`/`replace()` por padrão (sem
 * precisar de `v5Compat`, que era uma opção só do fork interno do
 * react-router). Ver `ROUTER-PLANO-TECNICO.md` para a medição completa.
 *
 * **Spike B (aprovado 2026-07-24):** confirmado por type-check isolado que
 * esta instância satisfaz estruturalmente a interface `Navigator` do
 * `react-router` v7 (`createHref`, `go`, `push`, `replace` — `encodeLocation`
 * é opcional) sem adaptador nenhum — a ressalva original deste comentário não
 * se confirmou. Exportada para uso como prop `navigator`/fonte de
 * `location`/`action` por `react/router-bridge.tsx`, consumida só via
 * `import()` dinâmico dentro do `render()` da rota migrada em `main.ts`
 * (nunca estaticamente) — `router.ts` continua sem importar `react-router`.
 */
export const sharedHistory = createBrowserHistory();

/**
 * Instrumentação temporária do Spike A (remover após a validação da suíte
 * completa) — prova, em runtime, os 2 asserts que o usuário pediu: exatamente
 * 1 chamada a `history.listen()` neste processo, e o dispatcher (`handle`)
 * chamado exatamente 1x por evento de navegação (nunca 0, nunca 2+).
 */
let listenCallCount = 0;
let dispatchCallCount = 0;
export function getSpikeAInstrumentation(): { listenCallCount: number; dispatchCallCount: number } {
  return { listenCallCount, dispatchCallCount };
}

/**
 * Extraído de `handle()` (ADR-045, auditoria da Sidebar 22-09-2026) — mesma
 * regra de casamento de sempre (string exata OU `RegExp.test`), agora
 * reutilizável por `getActiveRouteKey()` abaixo (consumida por
 * `react/layout/use-active-route-key.ts`), sem duplicar a lógica.
 */
export function matchRoute(routes: Route[], pathname: string): Route | undefined {
  return routes.find((r) => (typeof r.path === "string" ? r.path === pathname : r.path.test(pathname)));
}

/**
 * Rotas registradas pela chamada mais recente de `startRouter()` — só existe
 * para `getActiveRouteKey()` abaixo. Nenhuma outra função deste módulo lê
 * `registeredRoutes` fora de `startRouter`/`getActiveRouteKey`.
 */
let registeredRoutes: Route[] = [];

/**
 * `key` da rota cujo `path` casa a URL atual (`sharedHistory.location.
 * pathname`), ou `null` se nenhuma casar (caso 404). Fonte de
 * `useActiveRouteKey()` (`react/layout/use-active-route-key.ts`, ADR-045) —
 * item ativo da sidebar passa a derivar da URL real via `sharedHistory` em
 * vez de só da prop `activeRoute` que cada página passa manualmente (que só
 * reflete a navegação depois que o módulo da página monta, 392ms–10,7s
 * medidos localmente). `router.ts` continua sem importar React — este módulo
 * só expõe dado puro, `useSyncExternalStore` fica no hook.
 */
export function getActiveRouteKey(): string | null {
  return matchRoute(registeredRoutes, sharedHistory.location.pathname)?.key ?? null;
}

export function startRouter(
  appRoot: HTMLElement,
  routes: Route[],
  notFound: PageRenderer,
  mountChrome: (key: string, crumbs: Route["breadcrumb"], chrome?: Route["chrome"]) => HTMLElement,
  onFirstRender?: () => void,
  /**
   * Calcula o destino de um "hard redirect" (reload completo da aba via
   * `window.location.replace`) a partir do path atual — ex.: guard de zona
   * (usuário sem permissão de admin acessando `/painel/*`). Só CALCULA;
   * quem executa o `replace` é `handle()`, antes mesmo de casar rota —
   * assim nenhuma passagem de `mountChrome()`/`match.render()`/nav-progress/
   * splash roda para um ciclo que vai morrer no reload.
   */
  resolveHardRedirect?: (path: string) => string | null,
): void {
  registeredRoutes = routes;
  let firstRenderDone = false;

  async function handle() {
    dispatchCallCount++;
    const path = window.location.pathname;

    const hardRedirect = resolveHardRedirect?.(path);
    if (hardRedirect) {
      window.location.replace(hardRedirect);
      return;
    }

    const match = matchRoute(routes, path);

    // Barra de progresso de navegação (ADR-024) — só a partir da 2ª
    // navegação da aba (`firstRenderDone`), para não duplicar o splash
    // estático que já cobre a 1ª carga. `finally` garante o `stop()` em
    // sucesso, `notFound` e rejeição do `import()` dinâmico do chunk da rota.
    let renderResult: void | boolean = undefined;
    if (firstRenderDone) startNavProgress();
    try {
      if (match?.redirect === true) {
        // Rota de redirect interno da SPA: nunca vira UI visível, então não
        // monta chrome nenhuma — `render()` só recalcula o destino e dispara
        // history.replaceState + popstate sintético, que reentra
        // SINCRONAMENTE em `handle()` (chamada aninhada) antes desta `await`
        // resolver. `appRoot` é seguro aqui porque essas rotas ignoram o
        // parâmetro recebido.
        await match.render(appRoot);
      } else if (match) {
        const pageRoot = mountChrome(match.key, match.breadcrumb, match.chrome);
        renderResult = await match.render(pageRoot);
      } else {
        // Sunset ADR-009, Estágio 4 — `chrome: "react"` corta o último ponto
        // que ainda alcançava mountShell() Vanilla (ver `renderNotFoundReact`
        // em main.ts, que também monta via DashboardLayout React).
        const pageRoot = mountChrome("404", [{ label: "Página não encontrada" }], "react");
        await notFound(pageRoot);
      }
    } finally {
      if (firstRenderDone) stopNavProgress();
    }

    // Passagem de redirect: nunca finaliza o ciclo de 1ª renderização — quem
    // finaliza é a chamada aninhada (reentrante, disparada pelo próprio
    // redirect) quando a página real montar de verdade. Cobre tanto o
    // redirect ESTÁTICO (`Route.redirect: true`, decidido em tempo de
    // definição) quanto o DINÂMICO (`renderResult === true`, decidido em
    // runtime dentro do próprio `render()` — ex. `guarded()` em main.ts).
    if (match?.redirect === true || renderResult === true) return;

    // Só a 1ª navegação (carregamento inicial da SPA) precisa esconder o
    // splash — trocas de rota subsequentes não passam por ele. `match.render`
    // já resolveu aqui, mas para páginas React isso só significa que a árvore
    // foi montada — o carregamento de dados delas roda em `useEffect`, depois
    // do mount. `waitForInitialSplash()` segura a remoção do splash até a
    // página sinalizar (via `releaseInitialSplash()`, em splash-gate.ts) que
    // seus dados essenciais da 1ª pintura estão prontos; páginas que não
    // participam desse protocolo resolvem isso instantaneamente.
    if (!firstRenderDone) {
      firstRenderDone = true;
      await waitForInitialSplash();
      onFirstRender?.();
    }
  }

  listenCallCount++;
  if (listenCallCount > 1) {
    // Assert do usuário: um 2º startRouter() no mesmo processo indicaria bug
    // grave (duplo dispatcher) — history.listen() já lançaria por conta
    // própria, mas falhamos alto e cedo aqui também, antes disso acontecer.
    throw new Error("router.ts: startRouter() chamado mais de uma vez neste processo.");
  }
  sharedHistory.listen(() => {
    void handle();
  });

  // Intercepta cliques em links internos com data-route para rotear client-side
  document.addEventListener("click", (ev) => {
    const target = ev.target as HTMLElement;
    const a = target.closest<HTMLAnchorElement>("a[data-route]");
    if (!a) return;
    // ClickUp #868kt003c — item de sidebar visível-mas-inativo usa
    // `pointerEvents: "none"` (bloqueia clique do mouse) + `disabled` no
    // `ListItemButton`, que o MUI reflete como `aria-disabled="true"` no `<a>`
    // renderizado (ver layout/nav-item.tsx). `pointerEvents: "none"` não
    // impede ativação programática (ex.: Enter/Space via teclado ainda chega
    // aqui em alguns navegadores) — reforço explícito para não rotear.
    if (a.getAttribute("aria-disabled") === "true") return;
    const href = a.getAttribute("href");
    if (!href || /^(https?:|mailto:|tel:)/.test(href)) return;
    ev.preventDefault();
    if (window.location.pathname !== href) {
      sharedHistory.push(href);
    }
  });

  void handle();
  void appRoot; // appRoot usado externamente via mountChrome
}
